import {
  SeatingInfo,
  SeatingInfoProps,
} from 'src/components/common/SeatingInfo';
import { SeatingCell } from 'src/components/TableV2/cells/SeatingCell';
import { Content } from 'src/contexts/ContentContext';
import { CountBadge, Stack, StackProps } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { getListingGroupItemCount } from 'src/utils/inventoryUtils';
import {
  Feature,
  GroupType,
  Listing,
  ListingGroup,
  Marketplace,
} from 'src/WebApiController';

export const ListingDisplayV2 = ({
  listing,
  showOrgSeat,
  width,
  justifySeatingIconContent,
  allowDisplaySeatingOverride = false,
  surfaceListingInGroup,
}: {
  listing: Listing;
  width?: 'fit' | 'full';
  justifySeatingIconContent?: StackProps['justifyContent'];
  allowDisplaySeatingOverride?: boolean;
  surfaceListingInGroup?: boolean;
} & Pick<SeatingInfoProps, 'showOrgSeat'>) => {
  const hasDisplaySeatingOverrideFeature = useUserHasFeature(
    Feature.DisplayMarketplaceSeatingOverride
  );
  const listingGroup = listing as ListingGroup;
  let overrideSeating = undefined;

  if (allowDisplaySeatingOverride && hasDisplaySeatingOverrideFeature) {
    overrideSeating = listing.mkpListings?.find(
      (ml) => ml.mkp == Marketplace.StubHub
    );
  }

  const hasIgnoreDupFeature = useUserHasFeature(Feature.IgnoreDuplicate);
  const isDuplicate =
    !!listing?.dupListingId && (!hasIgnoreDupFeature || !listing?.ignoreDup);

  const { seating, seatTraits } = listing;

  return listingGroup.isLtGrp && listingGroup.groupType !== GroupType.Active ? (
    <Stack direction="row" gap="m" alignItems="center" width={width}>
      {listingGroup.groupType === GroupType.Inactive ? (
        <Content id={ContentId.Queued} />
      ) : listingGroup.groupType === GroupType.FullySold ? (
        <Content id={ContentId.Sold} />
      ) : listingGroup.groupType === GroupType.Invisible ? (
        <Content id={ContentId.HiddenDueToFilter} />
      ) : (
        <>
          <b>{listingGroup.groupName}</b>
          {surfaceListingInGroup && (
            <SeatingInfo
              {...seating}
              orgSeatFr={listing.orgSeatFr}
              orgSeatTo={listing.orgSeatTo}
              showOrgSeat={showOrgSeat}
              isSeatSaver={listing.isSeatSaver}
              isDuplicate={isDuplicate}
              isSoldOut={listing.availQty === 0}
              justifyIconContent={justifySeatingIconContent}
              showUnmappedSection={true}
              overrideSectionRaw={overrideSeating?.overrideSection}
              overrideRowRaw={overrideSeating?.overrideRow}
              overrideSeatsRaw={overrideSeating?.overrideSeats}
              singleLine
            />
          )}
        </>
      )}
      {listingGroup.groupType !== GroupType.Leader && (
        <CountBadge
          count={getListingGroupItemCount(listingGroup)}
          color="gray"
        />
      )}
    </Stack>
  ) : (
    <Stack direction="column" gap="s" width={width}>
      <SeatingCell
        {...seating}
        isSeatSaver={listing.isSeatSaver}
        userDefinedPrecision={undefined}
        seatTraits={seatTraits}
        isDuplicate={isDuplicate}
        isSoldOut={listing.availQty === 0}
        showOrgSeat={showOrgSeat}
        orgSeatFr={listing.orgSeatFr}
        orgSeatTo={listing.orgSeatTo}
        isUnmappedSection={!seating.rowId}
        showUnmappedSection={true}
        overrideSectionRaw={overrideSeating?.overrideSection}
        overrideRowRaw={overrideSeating?.overrideRow}
        overrideSeatsRaw={overrideSeating?.overrideSeats}
      />
    </Stack>
  );
};
